import { Trophy, Navigation, ChevronRight, Clock, Bike, Wallet, Star } from "lucide-react";

/**
 * "Top livreurs" leaderboard card linking to /leaderboard.
 */
export const LeaderboardCard = ({ myRank, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="bg-gradient-to-r from-[#0F1B33] via-[#0A2655] to-[#003380] border border-[#D4AF37]/30 rounded-[2.5rem] p-5 cursor-pointer hover:scale-[1.01] transition-transform"
      data-testid="my-leaderboard-card"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#D4AF37] to-[#B8962E] flex items-center justify-center shadow-lg shadow-[#D4AF37]/30">
            <Trophy className="w-7 h-7 text-[#0F172A]" />
          </div>
          <div>
            <p className="text-[10px] text-white/60 uppercase tracking-widest font-bold">
              Top Livreurs ARTEKNOS · semaine
            </p>
            <p className="text-base md:text-lg font-black text-white">
              {myRank?.rank
                ? `Vous êtes #${myRank.rank} sur ${myRank.total_couriers}`
                : "Première livraison = entrée au classement"}
            </p>
            {myRank?.stats && (
              <p className="text-[10px] text-white/60 mt-1">
                {myRank.stats.deliveries} livraisons · {myRank.stats.total_km} km ·{" "}
                {(myRank.stats.score || 0).toLocaleString("fr-FR")} pts
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-[#D4AF37] uppercase tracking-widest font-bold">
            Prime mensuelle
          </p>
          <p className="text-sm md:text-base font-black text-[#D4AF37]">10K · 5K · 3K F</p>
          <p className="text-[10px] text-white/60 mt-0.5">Top 3</p>
        </div>
      </div>
    </div>
  );
};

/**
 * Hot zones panel showing high-demand areas (currently mocked client side).
 */
export const HotZonesList = ({ visible }) => {
  if (!visible) return null;
  const zones = [
    { zone: "Plateau · Centre", time: "5 min", demand: "Haute" },
    { zone: "Almadies · Mermoz", time: "12 min", demand: "Moyenne" },
    { zone: "Médina · Sandaga", time: "8 min", demand: "Moyenne" },
  ];
  return (
    <div
      className="bg-slate-900 border border-slate-800 rounded-[3rem] p-6"
      style={{ transform: "translateZ(0)" }}
      data-testid="courier-hot-zones"
    >
      <div className="flex justify-between items-center mb-5">
        <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">
          Zones à forte demande
        </h4>
        <div className="bg-[#D4AF37]/15 px-3 py-1 rounded-full text-[#D4AF37] text-[9px] font-black uppercase">
          🔥 Bonus x1.5
        </div>
      </div>
      <div className="space-y-3">
        {zones.map((z) => (
          <div
            key={z.zone}
            className="flex items-center justify-between p-3 bg-white/[0.03] rounded-2xl border border-white/5 hover:border-[#D4AF37]/30 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-800/80 rounded-xl flex items-center justify-center">
                <Navigation size={16} className="text-[#D4AF37]" />
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-tight">{z.zone}</p>
                <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest">
                  {z.time} de votre position · {z.demand}
                </p>
              </div>
            </div>
            <ChevronRight size={16} className="text-slate-600" />
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Bento-style stats grid.
 * NOTE: utilise des couleurs solides (bg-slate-900) au lieu de bg-white/5
 * pour éviter les glitches GPU sur Chrome Android (rendu de rgba transparent).
 */
export const StatsGrid = ({ activeCount, totalDeliveries, creditBalance, rating }) => {
  const tiles = [
    {
      icon: Clock,
      iconClass: "text-[#D4AF37]",
      value: activeCount,
      label: "Courses en cours",
      testid: "stat-online-time",
    },
    {
      icon: Bike,
      iconClass: "text-emerald-400",
      value: totalDeliveries,
      label: "Courses terminées",
      testid: "stat-deliveries",
    },
    {
      icon: Wallet,
      iconClass: "text-blue-400",
      value: creditBalance.toLocaleString("fr-FR"),
      label: "Crédit ARTEKNOS",
      testid: "stat-credit",
    },
    {
      icon: Star,
      iconClass: "text-amber-400",
      value: (rating || 5).toFixed(2),
      label: "Note moyenne",
      testid: "stat-rating",
      iconFill: true,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.testid}
            className="bg-slate-900 border border-slate-800 rounded-3xl p-5"
            style={{ transform: "translateZ(0)" }}
            data-testid={t.testid}
          >
            <Icon
              size={20}
              className={`${t.iconClass} mb-3`}
              fill={t.iconFill ? "currentColor" : "none"}
            />
            <p className="text-xl md:text-2xl font-black italic">{t.value}</p>
            <p className="text-[9px] text-slate-500 font-bold uppercase tracking-widest mt-1">
              {t.label}
            </p>
          </div>
        );
      })}
    </div>
  );
};
